/**
 * MentionInput Component
 *
 * Textarea with @mention autocomplete functionality.
 * Detects @ character and shows dropdown of organization users.
 * Keyboard navigation: arrows, Enter to select, Esc to close.
 */

import { useState, useRef, useEffect, KeyboardEvent } from 'react';
import { useThemeColors } from '@/styles';
import { spacing, typography, radius } from '@/styles/design-tokens';
import { withOpacity } from '@/styles/colors/withOpacity';
import { AsyncButton } from '@/components/ui';
import type { OrganizationUser } from '@/types/user-management.types';

interface MentionInputProps {
  value: string;
  onChange: (value: string) => void;
  onSubmit: () => Promise<void>;
  orgUsers: OrganizationUser[];
  disabled?: boolean;
  isLoading?: boolean;
  maxLength?: number;
  placeholder?: string;
  submitLabel?: string;
  showSubmitButton?: boolean;
}

export default function MentionInput({
  value,
  onChange,
  onSubmit,
  orgUsers,
  disabled = false,
  isLoading = false,
  maxLength = 1000,
  placeholder = 'Write a comment... (use @ to mention someone)',
  submitLabel = 'Post Comment',
  showSubmitButton = true,
}: MentionInputProps) {
  const colors = useThemeColors();
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const itemRefs = useRef<(HTMLDivElement | null)[]>([]);

  const [showAutocomplete, setShowAutocomplete] = useState(false);
  const [mentionStartIndex, setMentionStartIndex] = useState(-1);
  const [filteredUsers, setFilteredUsers] = useState<OrganizationUser[]>([]);
  const [selectedIndex, setSelectedIndex] = useState(0);

  // Detect @ mentions and show autocomplete
  useEffect(() => {
    if (!textareaRef.current) return;

    const textarea = textareaRef.current;
    const cursorPos = textarea.selectionStart;
    const textBeforeCursor = value.substring(0, cursorPos);

    // Find the last @ before cursor
    const lastAtIndex = textBeforeCursor.lastIndexOf('@');

    if (lastAtIndex !== -1) {
      // Check if there's any whitespace between @ and cursor
      const textAfterAt = textBeforeCursor.substring(lastAtIndex + 1);
      const hasWhitespace = /\s/.test(textAfterAt);

      if (!hasWhitespace) {
        // Extract the query string after @
        const query = textAfterAt;
        setMentionStartIndex(lastAtIndex);
        setShowAutocomplete(true);
        setSelectedIndex(0);

        // Filter users by query (search by both username and display_name)
        const filtered = orgUsers.filter(
          user =>
            user.username.toLowerCase().includes(query.toLowerCase()) ||
            user.display_name.toLowerCase().includes(query.toLowerCase())
        );
        setFilteredUsers(filtered);
      } else {
        setShowAutocomplete(false);
      }
    } else {
      setShowAutocomplete(false);
    }
  }, [value, orgUsers]);

  // Scroll selected item into view when keyboard navigating
  useEffect(() => {
    if (showAutocomplete && itemRefs.current[selectedIndex]) {
      itemRefs.current[selectedIndex]?.scrollIntoView({
        behavior: 'smooth',
        block: 'nearest',
      });
    }
  }, [selectedIndex, showAutocomplete]);

  // Clear refs when dropdown closes
  useEffect(() => {
    if (!showAutocomplete) {
      itemRefs.current = [];
    }
  }, [showAutocomplete]);

  const handleTextareaChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    onChange(e.target.value);
  };

  const insertMention = (username: string) => {
    if (!textareaRef.current || mentionStartIndex === -1) return;

    const textarea = textareaRef.current;
    const cursorPos = textarea.selectionStart;

    // Replace from @ to cursor with @username
    const before = value.substring(0, mentionStartIndex);
    const after = value.substring(cursorPos);
    const newValue = `${before}@${username} ${after}`;

    onChange(newValue);
    setShowAutocomplete(false);

    // Set cursor position after the mention
    const newCursorPos = mentionStartIndex + username.length + 2; // +2 for @ and space
    setTimeout(() => {
      textarea.focus();
      textarea.setSelectionRange(newCursorPos, newCursorPos);
    }, 0);
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    // Submit on Cmd/Ctrl + Enter
    if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') {
      e.preventDefault();
      onSubmit();
      return;
    }

    // Handle autocomplete navigation
    if (showAutocomplete && filteredUsers.length > 0) {
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setSelectedIndex(prev =>
          prev < filteredUsers.length - 1 ? prev + 1 : prev
        );
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        setSelectedIndex(prev => (prev > 0 ? prev - 1 : prev));
      } else if (e.key === 'Enter' && selectedIndex >= 0) {
        e.preventDefault();
        insertMention(filteredUsers[selectedIndex].username);
      } else if (e.key === 'Escape') {
        e.preventDefault();
        setShowAutocomplete(false);
      }
    }
  };

  const charCount = value.length;
  const isOverLimit = charCount > maxLength;
  const charCountColor = isOverLimit ? colors.error : colors.text.secondary;

  return (
    <div style={{ position: 'relative' }}>
      <div style={{ position: 'relative' }}>
        <textarea
          ref={textareaRef}
          value={value}
          onChange={handleTextareaChange}
          onKeyDown={handleKeyDown}
          disabled={disabled}
          placeholder={placeholder}
          maxLength={maxLength}
          style={{
            width: '100%',
            minHeight: 'calc(80px * var(--sizing-multiplier, 1))',
            maxHeight: 'calc(200px * var(--sizing-multiplier, 1))',
            padding: spacing.sm,
            paddingBottom: 'calc(28px * var(--sizing-multiplier, 1))', // Space for char counter
            fontSize: typography.sizes.sm,
            fontFamily: 'inherit',
            lineHeight: typography.lineHeights.normal,
            color: colors.text.primary,
            backgroundColor: colors.background,
            border: `1px solid ${colors.border.default}`,
            borderRadius: radius.sm,
            resize: 'vertical',
            outline: 'none',
          }}
        />

        {/* Character counter */}
        <div
          style={{
            position: 'absolute',
            bottom: '8px',
            right: '12px',
            fontSize: typography.sizes.xs,
            color: charCountColor,
            userSelect: 'none',
          }}
        >
          {charCount}/{maxLength}
        </div>
      </div>

      {/* Autocomplete dropdown */}
      {showAutocomplete && filteredUsers.length > 0 && (
        <div
          ref={dropdownRef}
          style={{
            position: 'absolute',
            bottom: '100%',
            left: 0,
            right: 0,
            marginBottom: 'calc(4px * var(--sizing-multiplier, 1))',
            maxHeight: 'calc(200px * var(--sizing-multiplier, 1))',
            overflowY: 'auto',
            backgroundColor: colors.background,
            border: `1px solid ${colors.border.default}`,
            borderRadius: radius.sm,
            boxShadow: `0 4px 6px ${withOpacity(colors.text.primary, 10)}`,
            zIndex: 1000,
          }}
        >
          {filteredUsers.map((user, index) => (
            <div
              ref={el => (itemRefs.current[index] = el)}
              key={user.id}
              onClick={() => insertMention(user.username)}
              style={{
                padding: `${spacing.xs} ${spacing.sm}`,
                cursor: 'pointer',
                backgroundColor:
                  index === selectedIndex
                    ? colors.hover.background
                    : 'transparent',
                borderBottom:
                  index < filteredUsers.length - 1
                    ? `1px solid ${colors.border.default}`
                    : 'none',
              }}
              onMouseEnter={() => setSelectedIndex(index)}
            >
              <div
                style={{
                  fontSize: typography.sizes.sm,
                  color: colors.text.primary,
                }}
              >
                {user.display_name}
              </div>
              <div
                style={{
                  fontSize: typography.sizes.xs,
                  color: colors.text.secondary,
                }}
              >
                @{user.username}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Submit button and hint */}
      <div
        style={{
          marginTop: spacing.xs,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <div
          style={{
            fontSize: typography.sizes.xs,
            color: colors.text.secondary,
          }}
        >
          {navigator.platform.includes('Mac') ? 'Cmd' : 'Ctrl'}+Enter to submit
        </div>
        {showSubmitButton && (
          <AsyncButton
            onClick={onSubmit}
            disabled={disabled || !value.trim() || isOverLimit}
            isLoading={isLoading}
            size="sm"
            className="px-4"
          >
            {submitLabel}
          </AsyncButton>
        )}
      </div>
    </div>
  );
}
